import Fuse from 'fuse.js';

import { useGetDevices } from '@cobliteam/hooks/src/useEntity/useDevices';
import useDriver from '@cobliteam/hooks/src/useEntity/useDriver';
import useVehicle from '@cobliteam/hooks/src/useEntity/useVehicle';
import useVehicleGroup from '@cobliteam/hooks/src/useEntity/useVehicleGroup';
import diacritics from 'diacritics';
import sidebarDef from '../sidebarDef';
import { EnumCategory } from '../types';
import type { DataWithCategory, OramaIaWithCategory } from '../types';

const useFetchData = () => {
  // ## FIXME: fetch promiseAll
  const { data: vehicles, isPending: isPendingVehicles } = useVehicle().getVehicles({});
  const { data: drivers, isPending: isPendingDrivers } = useDriver().getDriversWithVehicles({});
  const { data: vehicleGroups, isPending: isPendingVehicleGroups } = useVehicleGroup().getVehicleGroups({});
  const { data: devices, isPending: isPendingDevices } = useGetDevices({});

  if (isPendingVehicles || isPendingDrivers || isPendingVehicleGroups || isPendingDevices) {
    return {
      isLoading: true,
      vehicles: [],
      drivers: [],
      vehicleGroups: [],
      devices: [],
      fuseVehicles: undefined,
      fuseDrivers: undefined,
      fuseVehicleGroups: undefined,
      fuseDevices: undefined,
      fuseOramaIa: undefined,
    };
  }

  const getCategory = (categoryId: EnumCategory) => {
    const group = sidebarDef.find((category) => category.categories.find((c) => c.id === categoryId));
    return group?.categories.find((c) => c.id === categoryId);
  };

  // ## transformers
  const vehiclesWithCategory =
    vehicles?.map((vehicle) => {
      return { ...vehicle, tagsIndex: vehicle.tags?.map((tag) => tag.name).join(', '), category: getCategory(EnumCategory.VEHICLES) };
    }) || [];

  const driversWithCategory =
    drivers?.map((driver) => {
      return { ...driver, category: getCategory(EnumCategory.DRIVERS) };
    }) || [];

  const groupsWithVehicles =
    vehicleGroups?.map((vehicleGroup) => {
      const newVehicles = vehicles?.filter((vehicle) => vehicleGroup.vehicleIds?.includes(vehicle.id));
      return { ...vehicleGroup, vehicles: newVehicles, category: getCategory(EnumCategory.VEHICLE_GROUPS) };
    }) || [];

  const devicesWithCategory =
    devices?.map((device) => {
      return { ...device, category: getCategory(EnumCategory.DEVICES) };
    }) || [];

  const oramaIaWithCategory = [
    {
      id: '1',
      query: 'Olá! Bem vindo a busca inteligente da Cobli.',
      content: 'Para ajudar você a encontrar qualquer informação relacionada à Cobli.\n É só buscar algo no campo de busca acima.',
      category: getCategory(EnumCategory.IA_ARTICLES),
    },
  ] as OramaIaWithCategory[];

  const dataWithCategory = [...oramaIaWithCategory, ...vehiclesWithCategory, ...driversWithCategory, ...groupsWithVehicles, ...devicesWithCategory] as DataWithCategory[];

  // ## fuse
  const fuseConfig = {
    getFn: (obj: any, path: string | string[]) => {
      const value = Fuse.config.getFn(obj, path);
      if (typeof value === 'string') {
        return diacritics.remove(value);
      }
      return value;
    },
  };

  const fuse = new Fuse(dataWithCategory || [], {
    ...fuseConfig,
    keys: [
      // vehicles
      'label',
      'id',
      'licensePlate',
      'cobliId',
      'tagsIndex',
      // drivers
      'name',
      'driverCode',
      'driverLicense',
      'registryId',
      'cpf',
      'identifier',
      // vehicleGroups
      'name',
      'id',
      'vehicles.label',
      'vehicles.id',
      'vehicles.licensePlate',
      'vehicles.cobliId',
      // devices
      'label',
      'id',
      'deviceId',
      'deviceCode',
      'deviceType',
      // orama ia
      'content',
      {
        name: 'query',
        weight: 2, // to be more relevant
      },
      // articles
      'title',
      'content',
    ],
  });

  return {
    isLoading: false,
    data: dataWithCategory || [],
    fuse,
  };
};

export default useFetchData;
